// src/utils/blog.js
// 블로그 관리 화면 공용 규칙 — 백엔드 app/schemas/blog.py 와 같은 제한을 쓴다.

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SLUG_MIN = 3;
export const SLUG_MAX = 120;

export const LIMITS = {
  title: 200,
  excerpt: 300,
  seoTitle: 70,
  seoDescription: 160,
  category: 40,
  tag: 30,
  tags: 10,
};

export const CATEGORY_SUGGESTIONS = ['운영 가이드', '티켓 판매', '마케팅', '제품 소식'];

export const BLOG_NAME = '게이트온 블로그';

/** slug 형식 검사 → 오류 메시지 (정상이면 '') */
export const validateSlug = (slug) => {
  const s = (slug || '').trim();
  if (!s) return 'slug 를 입력하세요.';
  if (s.length < SLUG_MIN || s.length > SLUG_MAX) return `slug 는 ${SLUG_MIN}~${SLUG_MAX}자여야 합니다.`;
  if (!SLUG_RE.test(s)) return '영문 소문자, 숫자, 하이픈(-)만 쓸 수 있고 하이픈으로 시작·끝나거나 연달아 쓸 수 없습니다.';
  return '';
};

/**
 * 제목에서 slug 제안 — 영문/숫자만 남긴다.
 * 한글 제목은 결과가 비므로 관리자가 영문 slug 를 직접 입력해야 한다 (빈 문자열 반환).
 */
export const suggestSlug = (title) =>
  (title || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-$/, '');

/** 쉼표로 구분된 태그 문자열 → 배열 (공백 제거, '#' 제거, 중복 제거) */
export const parseTags = (text) => {
  const seen = new Set();
  return (text || '')
    .split(',')
    .map((t) => t.trim().replace(/^#+/, '').trim())
    .filter((t) => {
      const k = t.toLowerCase();
      if (!t || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
};

export const plainText = (html) =>
  (html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

/** 한국어 기준 분당 약 500자(공백 제외) — 백엔드 reading_minutes 와 같은 계산 */
export const readingMinutes = (html) => {
  const n = plainText(html).replace(/\s/g, '').length;
  return Math.max(1, Math.ceil(n / 500));
};

/** API 날짜(KST naive ISO) → datetime-local 입력값 */
export const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : '');

/** '2026년 11월 3일' */
export const formatKoreanDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${y}년 ${m}월 ${d}일`;
};

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

/** 서버 렌더링 공개 페이지 주소 (운영: same-origin /blog/..., 로컬: 백엔드 포트) */
export const publicPostUrl = (slug) => `${API_URL}/blog/${slug}`;
export const publicBlogUrl = () => `${API_URL}/blog`;
/** 서버 렌더링 경로(/blog...) 전체 주소 — SPA 안에서 들어왔을 때 전체 페이지 로드용 */
export const serverPageUrl = (pathWithQuery) => `${API_URL}${pathWithQuery}`;

/** /uploads/... 같은 상대 경로 이미지를 미리보기에서 보이도록 (로컬 개발 시 백엔드 포트) */
export const assetUrl = (url) => (url && url.startsWith('/') && !url.startsWith('//') ? `${API_URL}${url}` : url);

/** 게시 상태 라벨: 초안 / 발행됨 / 예약됨(발행 시각이 미래) */
export const statusLabel = (post) => {
  if (!post || post.status !== 'published') return { key: 'draft', label: '초안' };
  if (post.is_live === false) return { key: 'scheduled', label: '예약됨' };
  return { key: 'published', label: '발행됨' };
};
